import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import { useRedirectUponFetchFailure } from "utils/common";
import { handleInvalidRequest } from "utils/sdk";
import { addQueryParams, reverse } from "utils/urls";

import toast from "components/Toast";
import { usePageTitle } from "components/usePageTitle";

import { useSignedUser } from "entities/Auth/sdk";
import { useDealDetails } from "entities/Deal/sdk";
import {
  MemoSnapshotCreationMethod,
  MemoTabChoices,
} from "entities/Memo/components/Tabs/constants";
import { memoPDFCreate, useMemoDealDetails } from "entities/Memo/sdk";

import PrintableMemoContent from "./components/PrintableMemoContent";

import "pages/Deal/Memo/memo.css";

const PrintableMemo = () => {
  const hasRun = useRef(false);
  const params = useParams<{
    dealId: string;
    memoId: string;
  }>();

  const dealId = Number(params.dealId);
  const memoId = Number(params.memoId);

  const {
    data: memo,
    error,
    isLoading: isFetchingMemo,
  } = useMemoDealDetails({
    dealId,
    memoId,
  });

  const { data: user } = useSignedUser();
  const { data: deal } = useDealDetails({ dealId });

  useRedirectUponFetchFailure({ error, loading: isFetchingMemo });

  usePageTitle(
    deal?.name && memo?.name
      ? `Builders Patch: ${deal.name} - ${memo?.name} - Memo`
      : "Builders Patch: Memo"
  );
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const snapshotCreationMethod = useMemo(() => {
    if (!_.isNil(searchParams) && searchParams.has("creationMethod")) {
      return searchParams.get("creationMethod") as MemoSnapshotCreationMethod;
    }
  }, [searchParams]);

  const generatePDF = useCallback(async () => {
    // Let the browser render the page before generating the PDF
    await new Promise((resolve) => setTimeout(resolve, 3000));

    const isCreatedThroughESignFlow =
      snapshotCreationMethod === MemoSnapshotCreationMethod.E_SIGN_PROCESS;

    memoPDFCreate({
      dealId,
      memoId,
      html: "<!DOCTYPE html>" + document.documentElement.outerHTML,
      isCreatedThroughESignFlow,
    })
      .then((response) => {
        if (!isCreatedThroughESignFlow) {
          toast.successMessage(
            "Currently we are generating a Memo PDF file. You will be notified, when the generation has finished."
          );
        }
        navigate(
          addQueryParams(
            reverse(URLS.DEAL_MEMO, {
              dealId,
              memoId,
            }),
            !_.isNil(snapshotCreationMethod)
              ? {
                  creationMethod: snapshotCreationMethod,
                  memoPDFId: response.id,
                }
              : {
                  tab: MemoTabChoices.SAVED_PDFS,
                }
          )
        );
      })
      .catch((error) => {
        handleInvalidRequest(error);
        navigate(
          addQueryParams(
            reverse(URLS.DEAL_MEMO, {
              dealId,
              memoId,
            }),
            !_.isNil(snapshotCreationMethod)
              ? {
                  creationMethod: snapshotCreationMethod,
                }
              : {
                  tab: MemoTabChoices.SAVED_PDFS,
                }
          )
        );
      });
  }, [dealId, memoId, navigate, snapshotCreationMethod]);

  useEffect(() => {
    if (_.isNil(memo) || hasRun.current) {
      return;
    }

    hasRun.current = true;

    generatePDF();
  }, [memoId, hasRun, memo, generatePDF]);

  if (!user || !memo || !deal) {
    return null;
  }

  return (
    <PrintableMemoContent
      deal={deal}
      memo={memo}
      companyLogo={user.company_logo}
    />
  );
};

export default PrintableMemo;
